// Pure, keystroke-cheap resolution of a (possibly qualified) column
// reference — `col`, `alias.col`, `tbl.col`, `schema.tbl.col` — to the
// owning snapshot table + column, honouring FROM/JOIN alias bindings.
// `fold` is the engine's identifier fold (Oracle upper-cases). Shared by
// all 5 dialect resolvers' enum + JSON-path collectors so the resolution
// logic exists once (DRY).
//
// `resolveRelation` is the dialect resolver's own rule for the table a
// relation reference names (PostgreSQL search_path — exact when read from the
// server, SQL Server default schema then `dbo`, the Oracle connected schema,
// the MySQL current database, SQLite by name). With it, an unqualified alias
// relation or `tbl.col` qualifier is the table the ENGINE reads — `FROM orders
// o WHERE o.status = '▮'` offers the default schema's enum values, never those
// of the first same-named table in the snapshot — and a bare `col` is looked
// up in the statement's bound relations first. Without it (hover, docs), the
// first same-named table owning the column is taken, as before.
//
// Bare single-segment refs no bound relation owns fall back to the first
// snapshot table that owns a column of that name (accepted ambiguity); a
// qualifier that doesn't resolve returns undefined — never a wrong-table
// match that would leak an unrelated table's enum/JSON hints.

import type { AliasBinding } from '@/core/engines';

export type RefResolvableColumn = { readonly name: string };
export type RefResolvableTable = {
  readonly name: string;
  readonly schema?: string;
  readonly columns: ReadonlyArray<RefResolvableColumn>;
};
export type ResolvedColumnRef<T extends RefResolvableTable> = {
  readonly table: T;
  readonly column: T['columns'][number];
};

const ALIAS_TABLE_KINDS = new Set(['table', 'view']);

/** A dialect resolver's table lookup: `name` / `schema` as the SQL text spells them (delimiters included). */
export type RelationResolver<T> = (name: string, schema: string | undefined) => T | undefined;

export function resolveColumnRefToTable<T extends RefResolvableTable>(
  columnRef: string,
  aliasBindings: readonly AliasBinding[],
  snapshotTables: readonly T[],
  fold: (id: string) => string,
  resolveRelation?: RelationResolver<T>,
): ResolvedColumnRef<T> | undefined {
  const parts = columnRef
    .split('.')
    .map((p) => p.trim())
    .filter((p) => p.length > 0);
  if (parts.length === 0) return undefined;

  const colFolded = fold(parts[parts.length - 1]!);
  const owns = (t: T): RefResolvableColumn | undefined =>
    t.columns.find((c) => fold(c.name) === colFolded);
  const firstMatch = (): ResolvedColumnRef<T> | undefined => {
    for (const t of snapshotTables) {
      const c = owns(t);
      if (c) return { table: t, column: c };
    }
    return undefined;
  };
  const pick = (t: T | undefined): ResolvedColumnRef<T> | undefined => {
    if (!t) return undefined;
    const c = owns(t);
    return c ? { table: t, column: c } : undefined;
  };

  // Bare `col` — the statement's own relations first (engine resolution),
  // then the accepted first-match across the snapshot.
  if (parts.length === 1) {
    if (resolveRelation) {
      for (const b of aliasBindings) {
        if (!ALIAS_TABLE_KINDS.has(b.kind)) continue;
        const bound = pick(resolveRelation(b.relation, b.schema || undefined));
        if (bound) return bound;
      }
    }
    return firstMatch();
  }

  // The table `name` (in `schema`) names: the engine's rule when the caller
  // has one, else the first same-named table that owns the column.
  const findTable = (name: string, schema?: string): T | undefined => {
    if (resolveRelation) return resolveRelation(name, schema);
    const nameFolded = fold(name);
    const schemaFolded = schema === undefined ? undefined : fold(schema);
    return snapshotTables.find(
      (t) =>
        fold(t.name) === nameFolded &&
        (schemaFolded === undefined || fold(t.schema ?? '') === schemaFolded) &&
        owns(t) !== undefined,
    );
  };

  if (parts.length === 2) {
    const qf = fold(parts[0]!);
    const alias = aliasBindings.find(
      (b) => ALIAS_TABLE_KINDS.has(b.kind) && fold(b.alias) === qf,
    );
    if (alias) {
      const byAlias = pick(findTable(alias.relation, alias.schema || undefined));
      if (byAlias) return byAlias;
    }
    const byTableName = pick(findTable(parts[0]!));
    if (byTableName) return byTableName;
    // Qualifier explicitly given but resolves to no alias/table: the user
    // named a relation that isn't in scope. Return unresolved rather than
    // leaking an unrelated table's columns (enum/JSON semantic hints).
    return undefined;
  }

  // 3+ parts: treat the last three as schema.table.col (DB-qualified
  // 4-part refs fold their leading db segment away — table+schema suffice).
  const byQualified = pick(findTable(parts[parts.length - 2]!, parts[parts.length - 3]!));
  if (byQualified) return byQualified;
  // Schema.table qualifier given but unresolved — same rule as 2-part.
  return undefined;
}

/**
 * `snapshotTables` with the statement's own table / view relations — resolved
 * by the engine's rule — moved to the front, in binding order. For collectors
 * that know only a column name (a JSON-chain LHS): a column the query's own
 * tables hold is theirs, not the first same-named table of the snapshot.
 */
export function boundTablesFirst<T>(
  aliasBindings: readonly AliasBinding[],
  snapshotTables: readonly T[],
  resolveRelation: RelationResolver<T> | undefined,
): readonly T[] {
  if (!resolveRelation) return snapshotTables;
  const bound: T[] = [];
  for (const b of aliasBindings) {
    if (!ALIAS_TABLE_KINDS.has(b.kind)) continue;
    const t = resolveRelation(b.relation, b.schema || undefined);
    if (t !== undefined && !bound.includes(t)) bound.push(t);
  }
  return bound.length === 0 ? snapshotTables : [...bound, ...snapshotTables.filter((t) => !bound.includes(t))];
}
